import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, RefreshCw, Megaphone, Save, Clapperboard, Eye, Monitor, Smartphone } from "lucide-react";
import { listAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement, getContent, previewAnnouncement } from "@/lib/api";
import { Input, Area, Select, ImageInput, VideoInput, Toggle } from "./fields";
import { FicheOverlay, ItemCard, CardGrid } from "./Fiche";

const MODES = [
  { value: "video", label: "Mode A — Vidéo de lancement plein écran" },
  { value: "popup", label: "Mode B — Notification flottante" },
];

const POSITION_LABELS = { br: "Bas à droite", bl: "Bas à gauche", tr: "Haut à droite", tl: "Haut à gauche" };
const POSITION_OPTIONS = [
  { value: "br", label: "Bas à droite" },
  { value: "bl", label: "Bas à gauche" },
  { value: "tr", label: "Haut à droite" },
  { value: "tl", label: "Haut à gauche" },
];
const TRIGGER_OPTIONS = [
  { value: "immediate", label: "Immédiate (dès l'arrivée sur le site)" },
  { value: "delayed", label: "Différée (après un délai)" },
];
const DELAY_PRESETS = [5, 10, 15, 30, 60];

const stateOf = (a) => {
  const today = new Date().toISOString().slice(0, 10);
  if (!a.enabled) return { label: "Désactivée", tone: "text-white/50 border-white/15 bg-black/60" };
  if (a.startDate && a.startDate > today) return { label: "Programmée", tone: "text-amber-300 border-amber-400/40 bg-black/60" };
  if (a.endDate && a.endDate < today) return { label: "Terminée", tone: "text-white/40 border-white/15 bg-black/60" };
  return { label: "En ligne", tone: "text-green-300 border-green-400/40 bg-black/60" };
};

// Résumé lisible des réglages de répétition (affiché sur les cartes).
const repeatSummary = (a) => {
  const parts = [];
  if (a.trigger === "delayed" && a.displayDelaySec > 0) parts.push(`après ${a.displayDelaySec} s`);
  if (a.autoHideSec > 0) parts.push(`visible ${a.autoHideSec} s`);
  if (a.reappearDelayMin > 0) parts.push(`revient après ${a.reappearDelayMin} min`);
  if (a.maxShowsPerSession > 0) parts.push(`max ${a.maxShowsPerSession}×/visite`);
  if (a.hideAfterCloseHours != null && Number(a.hideAfterCloseHours) !== 24) {
    parts.push(Number(a.hideAfterCloseHours) === 0 ? "fermeture = jamais revue" : `masquée ${a.hideAfterCloseHours} h après fermeture`);
  }
  parts.push("fermeture = jamais revue");
  return parts.length ? parts.join(" · ") : "Une fois par visiteur (24 h après fermeture)";
};

// Nom lisible de la destination (affiché sur les cartes).
const destinationLabel = (a, linkOptions) => {
  if (!a.linkType) return "";
  const opt = (linkOptions[a.linkType] || []).find((o) => o.value === a.linkId);
  if (opt) return opt.label;
  return a.ctaUrl || "";
};

const Field = ({ label, children }) => (
  <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">{label}</span>{children}</label>
);
const rawCls = "mt-2 w-full bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none";

// Construit la liste des destinations cliquables à partir du contenu du site (évite les URL saisies à la main).
// Les URLs correspondent aux routes réelles du site : le lien reste valide tant que le contenu existe.
const buildLinkOptions = (c) => ({
  formation: (c.formations?.items || []).map((f) => ({ value: f.slug || f.id, label: f.title || "Formation", url: `/formation/${f.slug || f.id}` })),
  projet: (c.realisations || []).map((p) => ({ value: p.slug || p.id, label: p.title || "Projet", url: `/projet/${p.slug || p.id}` })),
  film: (c.festivalFilms?.items || []).map((f) => ({ value: f.slug || f.id, label: f.title || "Film", url: `/festival/edition/${f.editionSlug || f.edition || ""}` })).filter((o) => !o.url.endsWith("/")),
  festival: [
    { value: "__festival", label: "Festival (page principale)", url: "/festival" },
    ...(c.festivalArchive?.editions || []).map((e) => ({ value: String(e.id || e.year), label: `Édition ${e.year}${e.title ? ` — ${e.title}` : ""}`, url: `/festival/edition/${e.id || e.year}` })),
  ],
  actualite: (c.news?.items || []).map((n) => ({ value: n.slug || n.id, label: n.title || "Actualité", url: `/actualites/${n.id || n.slug}` })),
  evenement: (c.events?.items || []).map((e) => ({ value: e.id, label: e.title || "Événement", url: "/calendrier" })),
  page: [
    { value: "accueil", label: "Accueil", url: "/" },
    { value: "formations", label: "Formations (liste)", url: "/formations" },
    { value: "festival", label: "Festival", url: "/festival" },
    { value: "galerie", label: "Galerie photo", url: "/galerie" },
    { value: "videos", label: "Vidéothèque", url: "/videos" },
    { value: "calendrier", label: "Calendrier / Agenda", url: "/calendrier" },
    { value: "rejoindre", label: "Nous rejoindre (candidatures)", url: "/rejoindre" },
    { value: "association", label: "L'Association (section)", url: "/#association" },
    { value: "contact", label: "Contact (section)", url: "/#contact" },
    ...(c.pages || []).filter((p) => p.published !== false).map((p) => ({ value: `p-${p.slug}`, label: `Page « ${p.title || p.slug} »`, url: `/p/${p.slug}` })),
  ],
});

const BLANK = { mode: "popup", title: "", message: "", image: "", imageFormat: "", videoUrl: "", videoUrlMobile: "", ctaLabel: "", ctaUrl: "", linkType: "", linkId: "", startDate: "", endDate: "", priority: 0, enabled: true, position: "br", trigger: "immediate", displayDelaySec: "", autoHideSec: "", reappearDelayMin: "", maxShowsPerSession: "", hideAfterCloseHours: "" };

export const AdminAnnouncements = () => {
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState(null); // null = liste ; objet = fiche ouverte
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [linkOptions, setLinkOptions] = useState({}); // {formation:[{value,label,url}], projet:[…], …}

  const load = async () => {
    setLoading(true);
    try {
      const [ann, content] = await Promise.all([listAnnouncements(), getContent().catch(() => null)]);
      setRows(ann);
      if (content) setLinkOptions(buildLinkOptions(content));
    } catch { toast.error("Impossible de charger les annonces."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!(draft.title || "").trim()) { toast.error("Donnez un titre à l'annonce."); return; }
    if (draft.mode === "video" && !(draft.videoUrl || "").trim()) { toast.error("Le Mode A nécessite une vidéo."); return; }
    setSaving(true);
    try {
      if (draft.id) await updateAnnouncement(draft.id, draft);
      else await createAnnouncement(draft);
      toast.success("Annonce enregistrée — effective immédiatement selon ses dates.");
      setDraft(null);
      await load();
    } catch { toast.error("Échec de l'enregistrement."); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try { await deleteAnnouncement(id); setRows((rs) => rs.filter((x) => x.id !== id)); toast.success("Annonce supprimée."); }
    catch { toast.error("Échec de la suppression."); }
  };

  // Aperçu fidèle : envoie le brouillon au backend (normalisé, NON sauvegardé),
  // le transmet au site en sessionStorage, puis ouvre le site en mode aperçu.
  const preview = async () => {
    if (!(draft.title || "").trim()) { toast.error("Donnez d'abord un titre à l'annonce."); return; }
    setSaving(true);
    try {
      const normalized = await previewAnnouncement(draft);
      sessionStorage.setItem("ks_ann_preview_payload", JSON.stringify(normalized));
      window.open("/?annPreview=1", "_blank");
      toast.success("Aperçu ouvert dans un nouvel onglet (non comptabilisé).");
    } catch { toast.error("Impossible de préparer l'aperçu."); }
    finally { setSaving(false); }
  };

  if (draft) {
    return (
      <FicheOverlay testid="announcement-fiche" title={draft.title || "Nouvelle annonce"}
        subtitle={draft.mode === "video" ? "Mode A · Vidéo de lancement plein écran" : "Mode B · Notification flottante (bas-droite)"}
        onClose={() => setDraft(null)}
        actions={
          <div className="flex items-center gap-2">
            <button onClick={preview} data-testid="announcement-preview"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white/80 text-[11px] tracking-[0.1em] uppercase hover:border-electric hover:text-electric transition-colors">
              <Eye size={13} /> Aperçu
            </button>
            <button onClick={save} disabled={saving} data-testid="announcement-save"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-electric text-black text-[11px] tracking-[0.1em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Enregistrer
            </button>
          </div>
        }>
        <Select label="Mode d'affichage" value={draft.mode} onChange={(v) => set("mode", v)} options={MODES} />
        <div className="rounded-lg border border-electric/20 bg-[#0a0a0a] p-4 text-xs text-secondary2">
          {draft.mode === "video"
            ? "Mode A : la vidéo s'affiche en plein écran à l'arrivée du visiteur, son coupé par défaut (bouton « Activer le son » visible). Idéale pour un lancement."
            : "Mode B : une notification élégante glisse en bas à droite de l'écran — image/vidéo, titre, texte, bouton — sans bloquer la navigation. Elle disparaît toute seule après la durée choisie."}
          {" "}Fermer ou cliquer <strong className="text-white">masque l'annonce pendant 24 h par défaut</strong> (réglable ci-dessous) ; une annonce disparue d'elle-même peut réapparaître automatiquement si vous l'activez. Jamais deux annonces en même temps.
        </div>
        <Input label={draft.mode === "popup" ? "Titre (affiché dans la notification)" : "Titre — identification interne uniquement (jamais affiché sur le site)"} value={draft.title} onChange={(v) => set("title", v)} placeholder="Ex : Annonce Festival 2026" />
        <Area label="Message affiché sur le site (optionnel)" value={draft.message} onChange={(v) => set("message", v)} rows={3} placeholder="Texte visible par les visiteurs, par-dessus la vidéo ou l'image" />
        {draft.mode === "video" ? (
          <>
            <VideoInput label="Vidéo — version ORDINATEUR (16:9 horizontal)" value={draft.videoUrl} onChange={(v) => set("videoUrl", v)} />
            <VideoInput label="Vidéo — version TÉLÉPHONE (9:16 vertical, optionnel)" value={draft.videoUrlMobile} onChange={(v) => set("videoUrlMobile", v)} />
            <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-3 text-xs text-secondary2">
              Fournissez deux fichiers spécialement préparés pour chaque format : le site utilisera automatiquement celui adapté à l'écran du visiteur. Si une seule version est renseignée, elle s'affichera proprement (sans étirement) sur tous les écrans.
            </div>
          </>
        ) : (
          <>
            <ImageInput label="Image de la notification (optionnel)" value={draft.image} onChange={(v) => set("image", v)} />
            {draft.image && (
              <Select label="Format de l'image / affiche" value={draft.imageFormat || ""} onChange={(v) => set("imageFormat", v)}
                options={[{ value: "", label: "Automatique (horizontal)" }, { value: "horizontal", label: "Horizontal / Paysage" }, { value: "vertical", label: "Vertical / Portrait" }, { value: "square", label: "Carré" }]} />
            )}
            <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-3 text-xs text-secondary2">
              Astuce : pour une <strong className="text-white">vidéo</strong> dans une annonce, utilisez le Mode A (plein écran, versions ordinateur 16:9 et téléphone 9:16 possibles). Le Mode B reste volontairement léger (image + texte + bouton) pour ne pas gêner la navigation.
            </div>
          </>
        )}
        {draft.mode === "video" && <ImageInput label="Affiche / poster avant lecture (optionnel)" value={draft.image} onChange={(v) => set("image", v)} />}

        {/* Destination cliquable : liste des contenus + lien manuel */}
        <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 space-y-3">
          <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Bouton d'action (optionnel — laissez vide pour une annonce informative)</span>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select label="Destination (rubrique / contenu du site)" value={draft.linkType || ""} onChange={(v) => set("linkType", v) + set("linkId", "") + set("ctaUrl", "")}
              options={[{ value: "", label: "— Aucune / manuelle —" }, { value: "formation", label: "Formation (fiche détaillée)" }, { value: "projet", label: "Projet / Réalisation" }, { value: "festival", label: "Festival / Édition" }, { value: "film", label: "Film du festival" }, { value: "actualite", label: "Actualité" }, { value: "evenement", label: "Événement" }, { value: "page", label: "Page interne" }, { value: "url", label: "Lien externe (URL)" }]} />
            {draft.linkType && draft.linkType !== "url" && (
              <Select label={`Choisir ${({ formation: "la formation", projet: "le projet", festival: "l'édition", film: "le film", actualite: "l'actualité", evenement: "l'événement", page: "la page" })[draft.linkType] || "le contenu"}`}
                value={draft.linkId || ""} onChange={(v) => { set("linkId", v); const opt = (linkOptions[draft.linkType] || []).find((o) => o.value === v); if (opt) set("ctaUrl", opt.url); }}
                options={[{ value: "", label: "— Sélectionner… —" }, ...(linkOptions[draft.linkType] || [])]} />
            )}
          </div>
          {draft.linkType === "url" && <Input label="URL externe (https://…)" value={draft.ctaUrl} onChange={(v) => set("ctaUrl", v)} placeholder="https://…" />}
          <Input label="Texte du bouton (laissez vide = pas de bouton)" value={draft.ctaLabel} onChange={(v) => set("ctaLabel", v)} placeholder="Découvrir la formation" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Début (vide = dès maintenant)"><input type="date" value={draft.startDate || ""} onChange={(e) => set("startDate", e.target.value)} className={rawCls} data-testid="announcement-start" /></Field>
          <Field label="Fin (vide = sans fin)"><input type="date" value={draft.endDate || ""} onChange={(e) => set("endDate", e.target.value)} className={rawCls} data-testid="announcement-end" /></Field>
          <Field label="Priorité (plus grand = affiché d'abord)"><input type="number" value={draft.priority ?? 0} onChange={(e) => set("priority", Number(e.target.value))} className={rawCls} data-testid="announcement-priority" /></Field>
        </div>

        {/* Position & déclenchement (Mode B uniquement) */}
        {draft.mode === "popup" && (
          <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 space-y-3">
            <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Position & déclenchement de la notification</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Select label="Position à l'écran" value={draft.position || "br"} onChange={(v) => set("position", v)} testid="announcement-position"
                options={[{ value: "br", label: "Bas à droite" }, { value: "bl", label: "Bas à gauche" }, { value: "tr", label: "Haut à droite" }, { value: "tl", label: "Haut à gauche" }]} />
              <Select label="Déclenchement de l'annonce" value={draft.trigger || "immediate"} onChange={(v) => set("trigger", v)} testid="announcement-trigger"
                options={[{ value: "immediate", label: "Apparition immédiate (à l'arrivée sur le site)" }, { value: "delayed", label: "Apparition différée (après un délai)" }]} />
            </div>
            {draft.trigger === "delayed" && (
              <Field label="Délai avant la première apparition (secondes) — ex : 10, 30, 60">
                <input type="number" min="1" placeholder="ex : 30" value={draft.displayDelaySec ?? ""} onChange={(e) => set("displayDelaySec", e.target.value === "" ? "" : Math.max(0, Number(e.target.value)))} className={rawCls} data-testid="announcement-display-delay" />
              </Field>
            )}
            <p className="text-[11px] text-secondary2 leading-relaxed">
              Le délai est compté à partir de l'arrivée du visiteur sur le site et <strong className="text-white">ne redémarre pas</strong> quand il change de page.
              Sur mobile, la notification occupe toute la largeur sans masquer la navigation (en bas) ni le menu (en haut).
              Le Mode A (vidéo plein écran) n'est pas concerné par la position : il couvre toujours tout l'écran.
            </p>
          </div>
        )}

        {/* Durée & répétition */}
        <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 space-y-3">
          <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Durée & répétition</span>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Field label="Durée visible (secondes, vide = jusqu'à fermeture)">
              <input type="number" min="0" placeholder="ex : 8" value={draft.autoHideSec ?? ""} onChange={(e) => set("autoHideSec", e.target.value === "" ? "" : Math.max(0, Number(e.target.value)))} className={rawCls} data-testid="announcement-autohide" />
            </Field>
            <Field label="Réapparaît après (minutes, vide = jamais)">
              <input type="number" min="0" placeholder="ex : 3" value={draft.reappearDelayMin ?? ""} onChange={(e) => set("reappearDelayMin", e.target.value === "" ? "" : Math.max(0, Number(e.target.value)))} className={rawCls} data-testid="announcement-reappear" />
            </Field>
            <Field label="Apparitions max par visite (vide = illimité)">
              <input type="number" min="1" placeholder="ex : 3" value={draft.maxShowsPerSession ?? ""} onChange={(e) => set("maxShowsPerSession", e.target.value === "" ? "" : Math.max(1, Number(e.target.value)))} className={rawCls} data-testid="announcement-max-shows" />
            </Field>
          </div>
          <p className="text-[11px] text-secondary2 leading-relaxed">
            Exemple : apparition après 10 s, visible 8 s, réapparition après 3 min, 3 fois max par visite.
            <strong className="text-white"> Fermer (croix) ou cliquer sur l'annonce la masque définitivement</strong> pour ce visiteur ;
            une annonce disparue d'elle-même (durée écoulée) peut revenir selon la fréquence choisie.
          </p>
        </div>
        <Toggle label="Annonce active" hint="Inactive = conservée mais jamais affichée sur le site." checked={!!draft.enabled} onChange={(v) => set("enabled", v)} testid="announcement-enabled" />
        <div className="flex justify-end pt-2">
          <button onClick={save} disabled={saving} data-testid="announcement-save-bottom"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-electric text-black text-[12px] tracking-[0.1em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer l'annonce
          </button>
        </div>
      </FicheOverlay>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-announcements">
      <div className="rounded-lg border border-electric/20 bg-[#0a0a0a] p-4 text-sm text-secondary2 flex items-start gap-3">
        <Megaphone size={18} className="text-electric shrink-0 mt-0.5" />
        <span>Créez des <strong className="text-white">annonces immersives</strong> : vidéo de lancement plein écran (Mode A) ou pop-up d'actualité (Mode B). Programmez-les par dates, hiérarchisez-les par priorité. Effet immédiat — pas besoin de « Publier ».</span>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Annonces ({(rows || []).length})</span>
        <div className="flex items-center gap-3">
          <button onClick={load} data-testid="announcements-refresh" className="text-white/60 hover:text-electric"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></button>
          <button onClick={() => setDraft({ ...BLANK })} data-testid="admin-add-announcement" className="inline-flex items-center gap-2 text-xs text-white/70 hover:text-electric"><Plus size={14} /> Nouvelle annonce</button>
        </div>
      </div>

      {rows === null ? (
        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-electric" /></div>
      ) : rows.length === 0 ? (
        <p className="text-secondary2 text-sm py-8 text-center">Aucune annonce. Cliquez sur « Nouvelle annonce ».</p>
      ) : (
        <CardGrid testid="announcements-grid">
          {rows.map((a) => {
            const st = stateOf(a);
            const dest = destinationLabel(a, linkOptions);
            const repeat = a.mode === "popup" ? repeatSummary(a) : "";
            const subtitleParts = [
              a.mode === "video" ? "Vidéo plein écran" : ({ br: "Bas-droite", bl: "Bas-gauche", tr: "Haut-droite", tl: "Haut-gauche" }[a.position] || "Bas-droite"),
              dest ? `→ ${dest}` : "",
              a.startDate || a.endDate ? `${a.startDate || "…"} → ${a.endDate || "…"}` : "",
            ].filter(Boolean);
            return (
              <ItemCard key={a.id} testid={`announcement-card-${a.id}`}
                image={a.image} title={a.title}
                subtitle={subtitleParts.join(" · ")}
                badges={[
                  { label: st.label, tone: st.tone },
                  { label: a.mode === "video" ? "Mode A" : "Mode B", tone: "text-electric border-electric/40 bg-black/60" },
                  ...(repeat ? [{ label: repeat, tone: "text-white/60 border-white/15 bg-black/60" }] : []),
                  ...(a.priority ? [{ label: `P${a.priority}`, tone: "text-white/60 border-white/15 bg-black/60" }] : []),
                ]}
                onOpen={() => setDraft({ ...a })}
                actions={
                  <button onClick={() => remove(a.id)} data-testid={`announcement-delete-${a.id}`} className="p-2 rounded border border-white/10 text-red-400/70 hover:text-red-400 hover:border-red-400/40"><Trash2 size={14} /></button>
                } />
            );
          })}
        </CardGrid>
      )}

      <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 text-xs text-secondary2 flex items-start gap-3">
        <Clapperboard size={16} className="text-electric shrink-0 mt-0.5" />
        <span><strong className="text-white">Mode A</strong> : la vidéo démarre en sourdine (exigence des navigateurs), le visiteur peut activer le son. <strong className="text-white">Mode B</strong> : notification glissante, position et déclenchement configurables par annonce (bas-droite par défaut). Fermer ou cliquer masque l'annonce pendant 24 h par défaut ; une annonce disparue d'elle-même peut réapparaître si vous l'activez. Jamais deux annonces en même temps.</span>
      </div>
    </div>
  );
};
